<template>
  <div>
    <PageHeaderBanner>
      <template #title>SDFile登録</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <div class="cmd00710-upload-form">
      <label class="text-weight-bold" style="font-size: 1.1rem">ファイルアップロード</label>
      <div class="row q-gutter-x-xs items-center">
        <div class="col">
          <q-file
            v-model="uploadFiles"
            outlined
            multiple
            append
            dense
            clearable
            use-chips
            accept=".sdf"
            @clear="uploadFiles = []"
          >
            <template #prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>
        </div>
        <div>
          <q-btn
            color="primary"
            label="アップロード"
            icon="mdi-upload"
            :disable="uploadFiles.length ? false : true"
            :class="uploadFiles.length ? '' : 'cmd00710-disabled-btn'"
            style="height: 40px"
            @click="displayUploadDialog"
          />
          <q-tooltip
            v-if="uploadFiles.length ? false : true"
            anchor="top middle"
            self="bottom middle"
            :offset="[15, 15]"
          >
            <strong>アップロードするファイルを選択してください</strong>
          </q-tooltip>
        </div>
      </div>
    </div>
    <div class="cmd00710-file-table">
      <label class="text-weight-bold" style="font-size: 1.1rem">アップロードSDFile一覧</label>
      <div
        :class="
          'chem-table-area btn-exist uploadfile-list ' +
          (fileData.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="fileData"
          :columns="fileColumns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>

          <!-- 編集削除Header -->
          <template #header-cell-editDelete>
            <q-th class="table-btn-area delete-btn-exists"></q-th>
          </template>
          <!-- 編集、削除Body -->
          <template #body-cell-editDelete="cell">
            <q-td v-if="!hideNoDataFlg" class="table-btn-area delete-btn-exists">
              <div class="flex">
                <q-btn
                  icon="mdi-pencil"
                  label="編集"
                  color="primary"
                  @click="editSdfFormat(cell.row.file_name)"
                />
                <q-btn
                  icon="mdi-close"
                  label="削除"
                  class="deletebtn"
                  color="primary"
                  outline
                  style="color: var(--q-delete)"
                  @click="displayDeleteUploadDialog(cell.row.file_name)"
                />
              </div>
            </q-td>
          </template>
          <template #body-cell="cell">
            <q-td v-if="!hideNoDataFlg" :props="cell">{{ cell.value }}</q-td>
          </template>
        </q-table>
      </div>
    </div>
    <div class="cmd00710-file-table">
      <label class="text-weight-bold" style="font-size: 1.1rem">SDFileインポート</label>
      <div class="row items-center">
        <label class="q-mr-lg">インポート処理日時：</label>
        <div class="q-gutter-sm flex">
          <q-radio v-model="scheduleType" val="1" label="即時実行" />
          <div class="flex">
            <q-radio v-model="scheduleType" val="0" label="処理日時指定" class="q-mr-md" />
            <div class="row q-gutter-sm">
              <q-input
                v-model="scheduleDate"
                outlined
                dense
                :disable="scheduleType === '1' ? true : false"
                class="date q-pb-none"
                readonly
                style="background: var(--q-white); width: 140px"
              >
                <template #append>
                  <q-icon name="event" class="cursor-pointer"></q-icon>
                </template>
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date v-model="scheduleDate" minimal no-unset mask="YYYY/MM/DD">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="OK" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-input>
              <q-input
                v-model="scheduleTime"
                outlined
                dense
                :disable="scheduleType === '1' ? true : false"
                class="date q-pb-none"
                readonly
                style="background: var(--q-white); width: 126px"
              >
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer"></q-icon>
                </template>
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-time v-model="scheduleTime" mask="HH:mm" :format24h="false">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="OK" color="primary" flat />
                    </div>
                  </q-time>
                </q-popup-proxy>
              </q-input>
            </div>
          </div>
        </div>
      </div>
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' +
          (importData.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="importData"
          :columns="importColumns"
          :visible-columns="visibleColumns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>

          <template #top>
            <div class="add-btn-area flex items-center filter-select">
              <q-select
                v-model="visibleColumns"
                multiple
                outlined
                dense
                options-dense
                display-value="カラム表示設定"
                emit-value
                map-options
                :options="visibleColumnOptions"
                option-value="name"
                color="primary"
                class="column-disp"
              />
            </div>
          </template>

          <!-- インポート開始、削除Header -->
          <template #header-cell-import>
            <q-th class="table-btn-area delete-btn-exists importbtn"></q-th>
          </template>

          <!-- インポート開始、削除Body -->
          <template #body-cell-import="cell">
            <q-td
              key="import"
              class="table-btn-area delete-btn-exists importbtn"
              style="text-align: center"
            >
              <div class="flex">
                <q-btn
                  color="primary"
                  label="インポート開始"
                  icon="mdi-update"
                  style="width: 160px"
                  @click="displayImportDialog(cell.row.catalog_id, cell.row.sup_sdfile_name)"
                />
                <q-btn
                  icon="mdi-close"
                  label="削除"
                  class="deletebtn"
                  outline
                  color="primary"
                  style="color: var(--q-delete)"
                  @click="displayDeleteImportDialog(cell.row.catalog_id, cell.row.sup_sdfile_name)"
                />
              </div>
            </q-td>
          </template>

          <template #body-cell="cell">
            <q-td :props="cell">{{ cell.value }} </q-td>
          </template>
        </q-table>
      </div>
    </div>
    <div class="cmd00710-log-table">
      <label class="text-weight-bold" style="font-size: 1.1rem">インポートログ</label>
      <div
        :class="
          'chem-table-area nofixed-column import-log ' +
          (logData.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          :rows="logData"
          :columns="logColumns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>

          <template #top>
            <q-btn
              color="primary"
              outline
              label="更新"
              icon="mdi-autorenew"
              class="import-reloadbtn"
              @click="reloadLog"
            />
          </template>

          <template #body="cell">
            <q-tr style="height: 50px">
              <q-td> {{ cell.row.sup_sdfile_name }} </q-td>
              <q-td
                :style="
                  isSdfErrExist(cell.row.status, cell.row.import_end_time) ? 'color: red' : ''
                "
              >
                <!-- 完了(エラー有り) -->
                <q-icon
                  v-if="
                    isSdfCompErrExist(cell.row.status, cell.row.import_end_time, cell.row.err_num)
                  "
                  size="20px"
                  name="mdi-alert"
                  style="color: var(--q-informationPublish)"
                />
                <!-- エラー系 -->
                <q-icon
                  v-else-if="isSdfErrExist(cell.row.status, cell.row.import_end_time)"
                  size="20px"
                  name="mdi-close-circle"
                />
                <!-- スケジュール済み -->
                <q-icon
                  v-else-if="cell.row.status === FileStatus.default.SCHEDULED"
                  size="20px"
                  name="mdi-calendar-check"
                />
                <!-- 完了 -->
                <q-icon
                  v-else-if="cell.row.status === FileStatus.default.COMPLETED"
                  size="20px"
                  name="mdi-check-circle"
                  style="color: green"
                />
                <!-- チェック中 -->
                <q-icon v-else size="20px" name="mdi-progress-check" />
                {{
                  isSdfCompErrExist(cell.row.status, cell.row.import_end_time, cell.row.err_num)
                    ? '完了(エラー有り)'
                    : FileStatus.getName(cell.row.status)
                }}
              </q-td>
              <q-td style="width: 160px"> {{ cell.row.exec_date_str }} </q-td>
              <q-td style="width: 160px"> {{ cell.row.import_start_time }} </q-td>
              <q-td style="width: 160px"> {{ cell.row.import_end_time }} </q-td>
              <q-td class="text-right">
                {{ cell.row.import_num ? commaFormat(cell.row.import_num) : cell.row.import_num }}
              </q-td>
              <q-td class="text-right">
                {{ cell.row.err_num ? commaFormat(cell.row.err_num) : cell.row.err_num }}
              </q-td>
              <q-td>
                <a
                  style="text-decoration: none; color: red"
                  :href="`/cmd007101/download-err-sdf?catalogId=${cell.row.catalog_id}`"
                >
                  <q-chip
                    v-if="cell.row.err_sdfile_name"
                    clickable
                    outline
                    square
                    color="red"
                    text-color="black"
                    icon="mdi-download"
                  >
                    {{ cell.row.err_sdfile_name }}
                  </q-chip>
                </a>
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </div>
    </div>
    <!-- アップロードダイアログ -->
    <AlertDialog
      :display="isUploadDisplayFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isUploadDisplayFlg = false"
      @confirm="checkUploadFile"
      @close="isUploadDisplayFlg = false"
    />
    <!-- アップロード削除ダイアログ -->
    <AlertDialog
      :display="isUploadDeleteDisplayFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isUploadDeleteDisplayFlg = false"
      @confirm="deleteUploadFile"
      @close="isUploadDeleteDisplayFlg = false"
    />
    <!-- インポート開始ダイアログ -->
    <AlertDialog
      :display="isImportDisplayFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isImportDisplayFlg = false"
      @confirm="importSdf"
      @close="isImportDisplayFlg = false"
    />
    <!-- インポート削除ダイアログ -->
    <AlertDialog
      :display="isDeleteImportDisplayFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isDeleteImportDisplayFlg = false"
      @confirm="deleteImportSdf"
      @close="isDeleteImportDisplayFlg = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import axios from '../../common/axios-common';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import * as FileStatus from '../../common/code/FileStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import commaFormat from '../../common/utils/numberUtils';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import { isFilesExist } from '../../common/utils/fileUtils';

// アップロードSDFile一覧
interface UploadSdfFileList {
  file_name: string;
  file_size: string;
  update_date: string;
}

// SDFileインポート
interface ImportSdfFileList {
  catalog_id: number;
  sup_sdfile_name: string;
  past_file_name: string;
  supplier_name: string;
  sup_category: string;
  memo: string;
}

// インポートログ
interface ImportLogList {
  catalog_id: number;
  sup_sdfile_name: string;
  status: string;
  exec_date_str: string;
  import_start_time: string;
  import_end_time: string;
  import_num: string;
  err_num: string;
  err_sdfile_name: string;
}

// カラム表示機能
interface VisibleColumn {
  name: string;
  label: string;
}

const router = useRouter();

// 表示カラム
const visibleColumns = ref<Array<string>>([]);
// カラム表示設定の選択肢
const visibleColumnOptions = ref<Array<VisibleColumn>>([]);
// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);

// Dialogタイトル
const dialogTitle = ref('');
// Dialog表示レベル
const dialogLevel = ref('');
// Dialog本文
const dialogText = ref(['']);
// 確認Dialogフラグ
const isConfFlg = ref(false);

// アップロードDialog表示フラグ
const isUploadDisplayFlg = ref(false);
// アップロード削除Dialog表示フラグ
const isUploadDeleteDisplayFlg = ref(false);
// インポートDialog表示フラグ
const isImportDisplayFlg = ref(false);
// インポート削除Dialog表示フラグ
const isDeleteImportDisplayFlg = ref(false);

// カタログID
const catalogId = ref(0);
// SDFファイル名
const sdfFileName = ref('');
// アップロードファイル
const uploadFiles = ref<File[]>([]);
// 処理スケジュールタイプ(0:処理日時指定 1:即時実行)
const scheduleType = ref('1');
// スケジュール日付
const scheduleDate = ref('');
// スケジュール時間
const scheduleTime = ref('');
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// アップロードSDFile一覧カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fileColumns: Array<any> = [
  {
    name: 'editDelete',
    field: (row: UploadSdfFileList) => row.file_name,
  },
  {
    name: ' file_name',
    label: 'SDFile名',
    align: 'left',
    field: (row: UploadSdfFileList) => row.file_name,
    sortable: true,
  },
  {
    name: 'file_size',
    label: 'ファイルサイズ',
    align: 'left',
    field: (row: UploadSdfFileList) => row.file_size,
    classes: 'text-right',
    sortable: true,
  },
  {
    name: 'update_date',
    label: '更新日時',
    align: 'left',
    field: (row: UploadSdfFileList) => row.update_date,
    style: 'width: 160px',
    sortable: true,
  },
];

// SDFileインポートカラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const importColumns: Array<any> = [
  {
    name: 'import',
    label: '',
    align: 'center',
    field: (row: ImportSdfFileList) => row.catalog_id,
    required: true,
  },
  {
    name: 'sup_sdfile_name',
    label: '登録SDFile名',
    align: 'left',
    field: (row: ImportSdfFileList) => row.sup_sdfile_name,
    sortable: true,
  },
  {
    name: 'past_file_name',
    label: '過去SDFile名',
    align: 'left',
    field: (row: ImportSdfFileList) => row.past_file_name,
    sortable: true,
  },
  {
    name: 'supplier_name',
    label: 'サプライヤ名',
    align: 'left',
    field: (row: ImportSdfFileList) => row.supplier_name,
    sortable: true,
  },
  {
    name: 'category_name',
    label: 'カテゴリー',
    align: 'left',
    field: (row: ImportSdfFileList) => row.sup_category,
    sortable: true,
  },
  {
    name: 'memo',
    label: '備考',
    align: 'left',
    field: (row: ImportSdfFileList) => row.memo,
    sortable: true,
  },
];

// インポートログカラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logColumns: Array<any> = [
  {
    name: 'sup_sdfile_name',
    label: 'SDFile名',
    align: 'left',
    field: (row: ImportLogList) => row.sup_sdfile_name,
    sortable: true,
  },
  {
    name: 'status',
    label: 'ステータス',
    align: 'left',
    field: (row: ImportLogList) => row.status,
    sortable: true,
  },
  {
    name: 'exec_date_str',
    label: '取込開始予定日時',
    align: 'left',
    field: (row: ImportLogList) => row.exec_date_str,
    sortable: true,
  },
  {
    name: 'import_start_time',
    label: '取込開始日時',
    align: 'left',
    field: (row: ImportLogList) => row.import_start_time,
    sortable: true,
  },
  {
    name: 'import_end_time',
    label: '取込完了日時',
    align: 'left',
    field: (row: ImportLogList) => row.import_end_time,
    sortable: true,
  },
  {
    name: 'import_num',
    label: '取込品目数',
    align: 'left',
    field: (row: ImportLogList) => row.import_num,
    sortable: true,
  },
  {
    name: 'err_num',
    label: 'エラー品目数',
    align: 'left',
    field: (row: ImportLogList) => row.err_num,
    sortable: true,
  },
  {
    name: 'err_sdfile_name',
    label: 'エラーSDFile',
    align: 'left',
    field: (row: ImportLogList) => row.err_sdfile_name,
    sortable: true,
  },
];

// アップロードSDFile一覧表示データ
const fileData = ref([
  {
    file_name: '',
    file_size: '',
    update_date: '',
  },
]);

// SDFileインポート表示データ
const importData = ref([
  {
    sup_sdfile_name: '',
    past_file_name: '',
    supplier_name: '',
    sup_category: '',
    memo: '',
  },
]);

// インポートログデータ
const logData = ref([
  {
    sup_sdfile_name: '',
    status: '',
    exec_date_str: '',
    import_start_time: '',
    import_end_time: '',
    import_num: '',
    err_num: '',
    err_sdfile_name: '',
  },
]);

/**
 * アップロード確認ダイアログ表示
 */
const displayUploadDialog = () => {
  dialogTitle.value = 'アップロード確認';
  dialogLevel.value = 'information';
  dialogText.value = ['選択したファイルをアップロードします。', 'よろしいですか？'];
  isConfFlg.value = false;
  isUploadDisplayFlg.value = true;
};

/**
 * アップロード完了ダイアログ
 */
const completeUploadDialog = () => {
  dialogTitle.value = 'アップロード完了';
  dialogLevel.value = 'information';
  dialogText.value = ['ファイルのアップロードが完了しました。'];
  isConfFlg.value = true;
  isUploadDisplayFlg.value = true;
};

/**
 * アップロード削除確認ダイアログ表示
 * @param {string} fileName ファイル名
 */
const displayDeleteUploadDialog = (fileName: string) => {
  sdfFileName.value = fileName;
  dialogTitle.value = '削除確認';
  dialogLevel.value = 'information';
  dialogText.value = [`${fileName}を削除します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isUploadDeleteDisplayFlg.value = true;
};

/**
 * インポート確認ダイアログ表示
 * @param {number} selCatalogId カタログID
 * @param {string} fileName ファイル名
 */
const displayImportDialog = (selCatalogId: number, fileName: string) => {
  // エラー初期化
  errMessage.value = '';
  isMessageChip.value = false;
  catalogId.value = selCatalogId;
  dialogTitle.value = 'スケジュール登録確認';
  dialogLevel.value = 'information';
  dialogText.value = [`${fileName}の取り込みスケジュールを登録します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isImportDisplayFlg.value = true;
};

/**
 * インポート完了ダイアログ
 */
const completeImportDialog = () => {
  dialogTitle.value = 'スケジュール登録完了';
  dialogLevel.value = 'information';
  dialogText.value = ['スケジュールの登録が完了しました。'];
  isConfFlg.value = true;
  isImportDisplayFlg.value = true;
};

/**
 * インポート削除確認ダイアログ表示
 * @param {number} selCatalogId 選択した行のカタログID
 * @param {string} fileName ファイル名
 */
const displayDeleteImportDialog = (selCatalogId: number, fileName: string) => {
  catalogId.value = selCatalogId;
  sdfFileName.value = fileName;
  dialogTitle.value = '削除確認';
  dialogLevel.value = 'information';
  dialogText.value = [`${fileName}を削除します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isDeleteImportDisplayFlg.value = true;
};

/**
 * 削除完了ダイアログ
 */
const completeDeleteDialog = () => {
  dialogTitle.value = '削除完了';
  dialogLevel.value = 'information';
  dialogText.value = ['削除が完了しました。'];
  isConfFlg.value = true;
};

/**
 * SDFile項目確認画面に遷移
 * @param {string} fileName ファイル名
 */
const editSdfFormat = (fileName: string) => {
  router.push({ name: 'sdfileSetFormat', params: { fileName } });
};

/**
 * 完了ステータスのSDFにエラーが存在するか判定
 * @param status  ファイルステータス
 * @param endDate 取込完了日時
 * @param errNum  エラー品目数
 * @returns エラーが存在する場合：true 存在しない場合：false
 */
const isSdfCompErrExist = (status: string, endDate: string, errNum: number) => {
  // Statusが完了かつ完了日時が入っていて、エラー品目数が1以上の場合
  if (status === FileStatus.default.COMPLETED && endDate && errNum > 0) {
    return true;
  }
  return false;
};

/**
 * SDFのエラーが存在するか判定
 * @param status  ファイルステータス
 * @param endDate 取込完了日時
 * @param errNum  エラー品目数
 * @returns エラーが存在する場合：true 存在しない場合：false
 */
const isSdfErrExist = (status: string, endDate: string) => {
  if (
    // エラー系Statusかつ完了日時が入っている場合
    (status === FileStatus.default.SDF_IMPORT_ERROR ||
      status === FileStatus.default.SYSTEM_ERROR ||
      status === FileStatus.default.REG_CHECK_ERROR) &&
    endDate
  ) {
    return true;
  }
  return false;
};

/**
 * 初期表示データ取得
 */
const getInitData = () => {
  // Loading Start
  Loading.show();

  axios
    .post('/cmd007101/init')
    .then((res) => {
      const { common, currentDate, currentTime, sdfTempList, sdfImportList, logList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          scheduleDate.value = currentDate;
          scheduleTime.value = currentTime;
          fileData.value = sdfTempList;
          importData.value = sdfImportList;
          logData.value = logList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

/**
 * ファイルアップロード
 */
const uploadFile = () => {
  isUploadDisplayFlg.value = false;
  // エラー初期化
  errMessage.value = '';
  isMessageChip.value = false;

  // Loading Start
  Loading.show();

  const formData = new FormData();
  Object.entries(uploadFiles.value).forEach(([, value]) => {
    formData.append('fileList', value);
  });

  axios
    .post('/cmd007101/sdf-upload', formData)
    .then((res) => {
      const { common, sdfTempList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = sdfTempList;
          uploadFiles.value = [];
          completeUploadDialog();
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * ファイルアップロードチェック
 */
const checkUploadFile = async () => {
  isFilesExist(uploadFiles.value).then((value) => {
    if (value) {
      uploadFile();
    } else {
      dialogTitle.value = 'ファイル読み込みエラー';
      dialogLevel.value = 'error';
      if (uploadFiles.value !== undefined && uploadFiles.value[0].size === 0) {
        dialogText.value = [
          '選択したファイルは内容が設定されていません。',
          'ファイルを再度選択してください。',
        ];
      } else {
        dialogText.value = [
          '更新または削除されたファイルがあるため、アップロードできませんでした。',
          'ファイルを再度選択してください。',
        ];
      }
      isConfFlg.value = true;
      isUploadDisplayFlg.value = true;
    }
  });
};

/**
 * アップロードファイル削除
 */
const deleteUploadFile = () => {
  isUploadDeleteDisplayFlg.value = false;
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('file_name', sdfFileName.value);

  axios
    .post('/cmd007101/delete-upload-file', formData)
    .then((res) => {
      const { common, sdfTempList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = sdfTempList;
          completeDeleteDialog();
          isUploadDisplayFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // ファイルが存在しない場合
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              fileData.value = sdfTempList;
              dialogTitle.value = 'ファイル削除エラー';
              dialogLevel.value = 'error';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isUploadDisplayFlg.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * SDFインポート
 */
const importSdf = () => {
  isImportDisplayFlg.value = false;
  // Loading Start
  Loading.show();

  const formData = new FormData();
  formData.append('catalog_id', String(catalogId.value));
  formData.append('schedule_type', scheduleType.value);
  formData.append('schedule_date', scheduleDate.value);
  formData.append('schedule_time', scheduleTime.value);

  axios
    .post('/cmd007101/import-sdf', formData)
    .then((res) => {
      const { common, sdfImportList, logList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          importData.value = sdfImportList;
          logData.value = logList;
          completeImportDialog();
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.DATE_FORMAT_ERROR:
              dialogTitle.value = 'エラー';
              dialogLevel.value = 'error';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isImportDisplayFlg.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * SDFインポート削除
 */
const deleteImportSdf = () => {
  isDeleteImportDisplayFlg.value = false;
  // Loading Start
  Loading.show();

  const formData = new FormData();
  formData.append('catalog_id', String(catalogId.value));

  axios
    .post('/cmd007101/delete-import-sdf', formData)
    .then((res) => {
      const { common, sdfTempList, sdfImportList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = sdfTempList;
          importData.value = sdfImportList;
          completeDeleteDialog();
          isDeleteImportDisplayFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * ログ再読み込み
 */
const reloadLog = () => {
  // Loading Start
  Loading.show();

  axios
    .post('/cmd007101/reload-log')
    .then((res) => {
      const { common, logList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          logData.value = logList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 初期表示処理
 */
getInitData();
// カラム表示設定
for (let i = 0; i < importColumns.length; i += 1) {
  visibleColumns.value.push(importColumns[i].name);
  // インポート開始ボタンのカラムは除外
  if (importColumns[i].name !== 'import') {
    visibleColumnOptions.value.push({
      name: importColumns[i].name,
      label: importColumns[i].label,
    });
  }
}
</script>
<style scoped>
.cmd00710-upload-form {
  margin: 0 0 40px 5%;
  width: 90%;
  max-width: unset;
  padding: 0;
}
.cmd00710-file-table,
.cmd00710-log-table {
  width: 90%;
  margin: 0 auto 30px;
}

.q-btn.import-reloadbtn {
  width: 100px;
}

.chem-table-area.btn-exist
  .q-table--dense
  .q-table
  tbody
  td.table-btn-area.delete-btn-exists.importbtn
  .flex,
.chem-table-area.btn-exist
  .q-table--dense
  .q-table
  thead
  th.table-btn-area.delete-btn-exists.importbtn
  .flex {
  width: 280px;
}
</style>

<style>
.cmd00710-file-table .chem-table-area .q-table__container {
  width: 100%;
}

.import-log.chem-table-area.nofixed-column .q-table__container,
.uploadfile-list.chem-table-area.btn-exist .q-table__container {
  margin-top: 3px;
}

.cmd00710-upload-form .cmd00710-disabled-btn {
  background-color: var(--q-opacityDisable) !important;
}

.cmd00710-log-table .chem-table-area .q-table__top.relative-position.row {
  left: unset;
  right: 0;
}

.cmd00710-log-table .chem-table-area .q-table__bottom.row {
  right: 100px;
}

.cmd00710-file-table .q-field--outlined.q-field--readonly .q-field__control:before {
  border-style: solid;
}

.cmd00710-file-table .q-radio__inner {
  margin-bottom: 2px;
}

.cmd00710-upload-form .q-field .q-field__control.row {
  align-items: center;
}
</style>
